import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Grid, Typography, Button, Slider, IconButton, Chip, Alert,
  Paper, Divider, CircularProgress, Tab, Tabs, TextField, Switch, FormControlLabel,
  LinearProgress, List, ListItem, ListItemText, ListItemIcon, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  ArrowUpward, ArrowDownward, ArrowBack, ArrowForward, Stop, Battery90, Speed,
  Memory, Storage, BatteryCharging20, Navigation, Cancel, CheckCircle, Save,
  Warning, LocationOn, PlayArrow, Tune, PowerSettingsNew, Thermostat, WaterDrop,
  HealthAndSafety, ElectricBolt, GpsFixed, GpsOff,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppHooks';
import { fetchMyRobots } from '../../features/robots/robotsSlice';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useTeleopController } from '../../hooks/useTeleopController';
import apiClient from '../../utils/apiClient';
import LiveMap from '../Maps/LiveMap';
import AutoDockSettings from './AutoDockSettings';
import { sendConfig, getConfig, addEventListener, removeEventListener, shutdownRobot, rebootRobot, navToWaypoint, getMapManagerStatus } from '../../utils/socketClient';
import { normalizeBatteryPercent } from '../../utils/battery';

interface RobotConfig {
  auto_charge_enabled: boolean;
  charge_threshold: number;
  max_speed: number;
  command_rate_hz: number;
  charging_dock: { x: number; y: number; theta: number } | null;
}

interface SavedWaypoint {
  id: string;
  name: string;
  x: number;
  y: number;
  theta: number;
}

const RobotControl: React.FC = () => {
  const { robotId } = useParams<{ robotId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { robots } = useAppSelector((state) => state.robots);
  const { robotsOnline, sendTeleop, sendNavGoal, cancelNavigation, emergencyStop: wsEmergencyStop, getRobotTelemetry, subscribeToRobot } = useWebSocket();

  const [activeTab, setActiveTab] = useState(0);
  const [maxSpeed, setMaxSpeed] = useState(0.5);
  const [navGoal, setNavGoal] = useState({ x: 0, y: 0, theta: 0 });
  const [config, setConfig] = useState<RobotConfig>({
    auto_charge_enabled: false,
    charge_threshold: 20,
    max_speed: 0.5,
    command_rate_hz: 10,
    charging_dock: null,
  });
  const [configSaved, setConfigSaved] = useState(false);
  const [waypoints, setWaypoints] = useState<SavedWaypoint[]>([]);
  const [latency, setLatency] = useState<number | null>(null);
  const [shutdownDialogOpen, setShutdownDialogOpen] = useState(false);
  const [rebootDialogOpen, setRebootDialogOpen] = useState(false);
  const lastTelemetryTime = useRef<number>(0);
  
  // Map Manager / Localization state
  const [mapManagerStatus, setMapManagerStatus] = useState({
    localized: false,
    hasChargerWp: false,
    currentMap: '',
    navStatus: 'idle'
  });

  useEffect(() => { if (robots.length === 0) dispatch(fetchMyRobots()); }, [dispatch, robots.length]);

  const robot = robots.find((r) => r.id === robotId);
  
  useEffect(() => { if (robot) subscribeToRobot(robot.serial_number); }, [robot, subscribeToRobot]);

  const telemetry = robot ? getRobotTelemetry(robot.serial_number) : null;
  const isOnlineFromList = robot ? robotsOnline.includes(robot.serial_number) : false;
  const hasRecentTelemetry = telemetry && (Date.now() - telemetry.timestamp < 10000);
  const isOnline = isOnlineFromList || !!hasRecentTelemetry;

  // Normalize pose from telemetry — handles multiple formats from cloud_bridge_node:
  // Format 1 (flat):    {pose: {x: 1.0, y: 2.0, theta: 0.5}}
  // Format 2 (nested):  {pose: {position: {x: 1.0, y: 2.0}, orientation: {z: 0.34, w: 0.94}}}
  // Format 3 (ROS2):    {pose: {pose: {position: {x, y}, orientation: {z, w}}}}
  const normalizedPose = React.useMemo(() => {
    if (!telemetry?.pose) return null;
    const p = telemetry.pose as any;
    
    // Format 1: flat {x, y, theta}
    if (typeof p.x === 'number') return p as { x: number; y: number; theta: number };
    
    // Format 3: double-nested {pose: {position, orientation}} (PoseWithCovarianceStamped)
    const inner = p.pose || p;
    const pos = inner.position || inner;
    const ori = inner.orientation;
    
    if (typeof pos.x !== 'number') return null;
    
    // Convert quaternion to yaw (theta)
    let theta = 0;
    if (ori && typeof ori.z === 'number' && typeof ori.w === 'number') {
      theta = 2 * Math.atan2(ori.z, ori.w);
    } else if (typeof p.theta === 'number') {
      theta = p.theta;
    }
    
    return { x: pos.x as number, y: pos.y as number, theta };
  }, [telemetry?.pose]);

  // Robot is localized ONLY when map_manager/AMCL says so. Having a valid
  // pose just means odometry is publishing — odometry-only pose is NOT
  // localization. The old (pose != null) fallback caused every online robot
  // to display "Localized" forever, even pre-AMCL-convergence.
  const isLocalized = !!mapManagerStatus.localized;

  // Use shared teleop controller hook
  const { activeKeys, handleKeyDown, handleKeyUp, emergencyStop: teleopStop, lastCommand } = useTeleopController({
    robotSerial: robot?.serial_number,
    isOnline: isOnline,
    sendTeleop,
    commandRateHz: config.command_rate_hz,
    maxSpeed,
    enabled: activeTab === 0,  // Only active on teleop tab
  });

  useEffect(() => {
    if (telemetry?.timestamp) {
      // telemetry.timestamp is ms since epoch (from robot's time.time() * 1000)
      const now = Date.now();
      const tsMs = typeof telemetry.timestamp === 'number' ? telemetry.timestamp : 0;
      if (tsMs > 1e12 && tsMs < now + 60000) {
        // Valid ms timestamp — calculate latency
        const calculatedLatency = Math.abs(now - tsMs);
        // Clamp to reasonable range (clock skew can cause huge values)
        setLatency(calculatedLatency < 30000 ? calculatedLatency : null);
      }
      lastTelemetryTime.current = now;
    }
  }, [telemetry]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (lastTelemetryTime.current && Date.now() - lastTelemetryTime.current > 5000) {
        setLatency(null);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Listen for robot_config (stable listener - added once)
  const [currentMapId, setCurrentMapId] = useState<string | null>(null);
  
  useEffect(() => {
    if (!robot) return;
    
    const handleRobotConfig = (data: any) => {
      console.log('RobotControl: robot_config received', data);
      const matchesRobot = !data.robot_serial || data.robot_serial === robot.serial_number;
      if (matchesRobot && data.config) {
        const cfg = data.config;
        console.log('RobotControl: Applying config keys:', Object.keys(cfg));
        setConfig(prev => ({
          ...prev,
          ...(cfg.auto_charge_enabled !== undefined && { auto_charge_enabled: cfg.auto_charge_enabled }),
          ...(cfg.charge_threshold !== undefined && { charge_threshold: cfg.charge_threshold }),
          ...(cfg.max_speed !== undefined && { max_speed: cfg.max_speed }),
          ...(cfg.max_linear_speed !== undefined && { max_speed: cfg.max_linear_speed }),
          ...(cfg.command_rate_hz !== undefined && { command_rate_hz: cfg.command_rate_hz }),
          ...(cfg.charge_dock_x !== undefined && { charging_dock: {
            x: cfg.charge_dock_x,
            y: cfg.charge_dock_y || 0,
            theta: cfg.charge_dock_theta || 0
          }}),
        }));
        if (cfg.max_speed || cfg.max_linear_speed) {
          setMaxSpeed(cfg.max_speed ?? cfg.max_linear_speed);
        }
        // Extract current_map for waypoints loading
        // Always look up by name — current_map_id from config can be stale/invalid
        const mapName = cfg.current_map;
        if (mapName) {
          apiClient.get(`/maps/robot/${robot.id}`).then(response => {
            const maps = response.data;
            const matched = maps.find((m: any) => m.name === mapName);
            if (matched) {
              console.log('RobotControl: Matched current_map by name:', mapName, '→', matched.id);
              setCurrentMapId(matched.id);
            } else {
              console.log('RobotControl: No map found matching name:', mapName);
            }
          }).catch(() => {});
        }
      }
    };
    
    addEventListener('robot_config', handleRobotConfig);
    
    return () => {
      removeEventListener('robot_config', handleRobotConfig);
    };
  }, [robot?.serial_number, robot?.id]);
  
  // Request config when online or load from DB cache when offline
  useEffect(() => {
    if (!robot) return;
    
    if (isOnline) {
      // Request live config from robot
      getConfig(robot.serial_number);
    } else {
      // Load cached config from DB (robot_config column from API)
      const cachedConfig = (robot as any).robot_config;
      if (cachedConfig && typeof cachedConfig === 'object') {
        console.log('RobotControl: Loading cached config from DB', Object.keys(cachedConfig));
        const cfg = cachedConfig;
        setConfig(prev => ({
          ...prev,
          ...(cfg.auto_charge_enabled !== undefined && { auto_charge_enabled: cfg.auto_charge_enabled }),
          ...(cfg.charge_threshold !== undefined && { charge_threshold: cfg.charge_threshold }),
          ...(cfg.max_speed !== undefined && { max_speed: cfg.max_speed }),
          ...(cfg.max_linear_speed !== undefined && { max_speed: cfg.max_linear_speed }),
          ...(cfg.command_rate_hz !== undefined && { command_rate_hz: cfg.command_rate_hz }),
        }));
        if (cfg.max_speed || cfg.max_linear_speed) {
          setMaxSpeed(cfg.max_speed ?? cfg.max_linear_speed);
        }
      }
    }
  }, [robot?.serial_number, robot?.id, isOnline, activeTab]);

  // Extract waypoints from robot metadata when robots state changes
  useEffect(() => {
    if (!robot) return;
    
    const robotMeta = robot?.robot_metadata || robot?.metadata;
    if (robotMeta) {
      const metadata = robotMeta as any;
      if (metadata.config) {
        setConfig(prev => ({ ...prev, ...metadata.config }));
      }
      if (metadata.loaded_map_waypoints && metadata.loaded_map_waypoints.length > 0) {
        console.log('RobotControl: Loading waypoints from metadata:', metadata.loaded_map_waypoints);
        setWaypoints(metadata.loaded_map_waypoints);
      } else if (metadata.waypoints && metadata.waypoints.length > 0) {
        console.log('RobotControl: Loading waypoints from legacy metadata:', metadata.waypoints);
        setWaypoints(metadata.waypoints);
      }
    }
  }, [robot?.robot_metadata, robot?.metadata, robots]); // Added robots dependency
  
  // Fetch waypoints from current loaded map
  useEffect(() => {
    const fetchCurrentMapWaypoints = async () => {
      if (!robot || !currentMapId) return;
      
      try {
        console.log('RobotControl: Fetching waypoints for map:', currentMapId);
        const response = await apiClient.get(`/maps/${currentMapId}`);
        const mapData = response.data;
        
        if (mapData.annotations?.waypoints && mapData.annotations.waypoints.length > 0) {
          const formattedWaypoints = mapData.annotations.waypoints.map((wp: any, index: number) => ({
            id: wp.id || `wp_${index}`,
            name: wp.name || `Waypoint ${index + 1}`,
            x: wp.x || 0,
            y: wp.y || 0,
            theta: wp.theta || 0
          }));
          console.log('RobotControl: Loaded', formattedWaypoints.length, 'waypoints from map');
          setWaypoints(formattedWaypoints);
        } else {
          console.log('RobotControl: Map has no waypoints annotations');
          setWaypoints([]);
        }
      } catch (error) {
        console.log('RobotControl: Could not fetch map waypoints:', error);
      }
    };
    
    fetchCurrentMapWaypoints();
  }, [robot?.id, currentMapId]);
  
  // Listen for map_loaded event to refresh waypoints
  useEffect(() => {
    const handleMapLoaded = (data: any) => {
      console.log('RobotControl: map_loaded event', data);
      if (data.robot_serial === robot?.serial_number) {
        // If waypoints are included in the event, use them immediately
        if (data.waypoints && Array.isArray(data.waypoints) && data.waypoints.length > 0) {
          const formattedWaypoints = data.waypoints.map((wp: any, index: number) => ({
            id: wp.id || `wp_${index}`,
            name: wp.name || `Waypoint ${index + 1}`,
            x: wp.x || 0,
            y: wp.y || 0,
            theta: wp.theta || 0
          }));
          setWaypoints(formattedWaypoints);
          console.log('RobotControl: Set waypoints from event:', formattedWaypoints);
        }
        // Refresh robot data to get updated metadata (which includes waypoints)
        // Small delay to ensure backend has committed the changes
        setTimeout(() => {
          dispatch(fetchMyRobots()).then(() => {
            console.log('RobotControl: Robots refreshed after map load');
          });
        }, 1500);
      }
    };
    
    addEventListener('map_loaded', handleMapLoaded);
    return () => removeEventListener('map_loaded', handleMapLoaded);
  }, [robot?.serial_number, dispatch]);

  // Listen for waypoints_saved event to refresh waypoints after add/delete in map editor
  useEffect(() => {
    const handleWaypointsSaved = (data: any) => {
      console.log('RobotControl: waypoints_saved event', data);
      if (data.robot_serial === robot?.serial_number) {
        // If waypoints are included in the event, use them directly
        if (data.waypoints && Array.isArray(data.waypoints)) {
          const formattedWaypoints = data.waypoints.map((wp: any, index: number) => ({
            id: wp.id || `wp_${index}`,
            name: wp.name || `Waypoint ${index + 1}`,
            x: wp.x || 0,
            y: wp.y || 0,
            theta: wp.theta || 0
          }));
          setWaypoints(formattedWaypoints);
          console.log('RobotControl: Updated waypoints from event:', formattedWaypoints);
        } else {
          // Fallback: refresh robot data to get updated waypoints
          setTimeout(() => {
            dispatch(fetchMyRobots()).then(() => {
              console.log('RobotControl: Robots refreshed after waypoints saved');
            });
          }, 500);
        }
      }
    };
    
    // Also listen for keepout_saved as map might have been updated
    const handleKeepoutSaved = (data: any) => {
      console.log('RobotControl: keepout_saved event', data);
      if (data.robot_serial === robot?.serial_number) {
        setTimeout(() => dispatch(fetchMyRobots()), 500);
      }
    };
    
    addEventListener('waypoints_saved', handleWaypointsSaved);
    addEventListener('keepout_saved', handleKeepoutSaved);
    
    return () => {
      removeEventListener('waypoints_saved', handleWaypointsSaved);
      removeEventListener('keepout_saved', handleKeepoutSaved);
    };
  }, [robot?.serial_number, dispatch]);

  // Config listeners

  // Listen for map_manager status events
  useEffect(() => {
    const handleMapManagerStatus = (data: any) => {
      if (data.robot_serial === robot?.serial_number) {
        setMapManagerStatus({
          localized: data.localized || false,
          hasChargerWp: data.has_charger_wp || false,
          currentMap: data.current_map || '',
          navStatus: data.nav_status || 'idle'
        });
      }
    };
    
    const handleMmNavStatus = (data: any) => {
      if (data.robot_serial === robot?.serial_number) {
        setMapManagerStatus(prev => ({ ...prev, navStatus: data.status }));
        // Clear waypoint highlight on terminal states
        const s = String(data.status || '').toLowerCase();
        if (s === 'succeeded' || s === 'aborted' || s === 'canceled' || s === 'cancelled' || s === 'idle') {
          setActiveWaypointName(null);
        }
      }
    };
    
    addEventListener('map_manager_status', handleMapManagerStatus);
    addEventListener('mm_nav_status', handleMmNavStatus);
    
    // Request initial status
    if (robot?.serial_number && isOnline) {
      setTimeout(() => getMapManagerStatus(robot.serial_number), 1000);
    }
    
    return () => {
      removeEventListener('map_manager_status', handleMapManagerStatus);
      removeEventListener('mm_nav_status', handleMmNavStatus);
    };
  }, [robot?.serial_number, isOnline]);

  // Keyboard event handlers
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isOnline || activeTab !== 0) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      
      const key = e.key.toLowerCase();
      if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright', ' '].includes(key)) {
        e.preventDefault();
        handleKeyDown(key);
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright', ' '].includes(key)) {
        handleKeyUp(key);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [isOnline, activeTab, handleKeyDown, handleKeyUp]);

  const handleEmergencyStop = useCallback(() => {
    console.log('EMERGENCY STOP triggered');
    teleopStop();
    if (robot) {
      // Send multiple stop commands for reliability
      cancelNavigation(robot.serial_number);
      wsEmergencyStop(robot.serial_number);
      // Also send zero velocity teleop command directly
      sendTeleop(robot.serial_number, 0, 0);
      console.log('Emergency stop commands sent to:', robot.serial_number);
    }
  }, [robot, teleopStop, cancelNavigation, wsEmergencyStop, sendTeleop]);

  const handleNavGoal = () => {
    if (robot && isOnline) {
      const thetaRad = navGoal.theta * Math.PI / 180;
      sendNavGoal(robot.serial_number, navGoal.x, navGoal.y, thetaRad);
    }
  };

  const handleCancelNav = () => { if (robot) cancelNavigation(robot.serial_number); };

  // Track which waypoint the user just dispatched the robot to.
  // Used purely for highlighting the card — cleared when nav completes/cancels.
  const [activeWaypointName, setActiveWaypointName] = useState<string | null>(null);

  const handleGoToWaypoint = (waypoint: SavedWaypoint) => {
    if (!robot || !isOnline) return;
    if (!isLocalized) return;
    setActiveWaypointName(waypoint.name);
    if (waypoint.name) {
      navToWaypoint(robot.serial_number, waypoint.name);
    } else {
      sendNavGoal(robot.serial_number, waypoint.x, waypoint.y, waypoint.theta);
    }
  };

  const handleSaveConfig = async () => {
    try {
      // Save to DB — admin endpoint also pushes to robot via WebSocket if connected
      await apiClient.patch(`/admin/robots/${robotId}`, { robot_config: config });
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 3000);
    } catch (error) { 
      console.error('Failed to save config via API:', error);
      // API failed — try direct WebSocket as fallback
      if (robot) {
        sendConfig(robot.serial_number, config);
        setConfigSaved(true);
        setTimeout(() => setConfigSaved(false), 3000);
      }
    }
  };

  if (!robot) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><CircularProgress /></Box>;

  const batteryPercent = normalizeBatteryPercent(telemetry?.battery?.percentage) ?? 0;
  const isCharging = telemetry?.battery?.is_charging || false;
  const batteryVoltage = telemetry?.battery?.voltage || 0;
  const batteryCurrent = telemetry?.battery?.current || 0;
  const batteryTemp = telemetry?.battery?.temperature || 0;
  const powerStatus = telemetry?.battery?.power_supply_status || 'unknown';
  const powerHealth = telemetry?.battery?.power_supply_health || 'unknown';
  const batteryLocation = telemetry?.battery?.location || '';
  const batterySerial = telemetry?.battery?.serial_number || '';
  
  const cpuPercent = telemetry?.diagnostics?.cpu_percent || 0;
  const memoryMb = telemetry?.diagnostics?.memory_mb || telemetry?.diagnostics?.memory_available_mb || 0;
  const memoryPercent = telemetry?.diagnostics?.memory_percent || 0;
  const memoryTotalMb = telemetry?.diagnostics?.memory_total_mb || 0;
  
  const envTemp = telemetry?.environment?.temperature || 0;
  const envHumidity = telemetry?.environment?.humidity || 0;
  const robotHealthy = telemetry?.environment?.is_healthy ?? true;
  const firmwareVersion = telemetry?.environment?.firmware_version || robot?.firmware_version || '';
  
  const formatMemory = (mb: number) => mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(0)} MB`;
  
  const handleShutdown = () => {
    if (robot) {
      shutdownRobot(robot.serial_number);
      setShutdownDialogOpen(false);
    }
  };
  
  const handleReboot = () => {
    if (robot) {
      rebootRobot(robot.serial_number);
      setRebootDialogOpen(false);
    }
  };

  const getLatencyColor = () => {
    if (latency === null) return 'text.secondary';
    if (latency < 100) return 'success.main';
    if (latency < 300) return 'warning.main';
    return 'error.main';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/robots')}><ArrowBack /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={700} sx={{ wordBreak: 'break-all' }}>{robot.serial_number}</Typography>
          <Typography variant="body2" color="text.secondary">{robot.model_number}{robot.friendly_name ? ` • ${robot.friendly_name}` : ''}</Typography>
        </Box>
        <Tooltip title={latency !== null ? `Latency: ${latency.toFixed(0)}ms` : 'No telemetry'}>
          <Chip size="small" label={latency !== null ? `${latency.toFixed(0)}ms` : '--'} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: getLatencyColor() }} />
        </Tooltip>
        {/* Localization status chip */}
        <Tooltip title={isLocalized ? 'Robot is localized' : 'Robot needs localization'}>
          <Chip 
            icon={isLocalized ? <GpsFixed /> : <GpsOff />} 
            label={isLocalized ? 'Localized' : 'Not Localized'} 
            color={isLocalized ? 'success' : 'warning'}
            size="small"
          />
        </Tooltip>
        <Chip icon={isCharging ? <BatteryCharging20 /> : <Battery90 />} label={`${batteryPercent.toFixed(0)}%`} color={batteryPercent > 50 ? 'success' : batteryPercent > 20 ? 'warning' : 'error'} />
        <Chip label={isOnline ? 'Online' : 'Offline'} color={isOnline ? 'success' : 'error'} />
      </Box>
      
      {/* Localization Banner - Show when robot is online but not localized */}
      {isOnline && !isLocalized && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Robot not localized.</strong> The robot needs to be localized on the map before navigation can start. This is handled by the robot's onboard systems.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>Health Status</Typography>
                <Chip 
                  icon={<HealthAndSafety />} 
                  label={robotHealthy ? 'Healthy' : 'Warning'} 
                  color={robotHealthy ? 'success' : 'warning'} 
                  size="small" 
                />
              </Box>
              
              {/* Battery Section */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{isCharging ? <BatteryCharging20 color="success" /> : <Battery90 />}<Typography variant="body2">Battery</Typography></Box>
                  <Typography variant="body2" fontWeight={600}>{batteryPercent.toFixed(1)}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={batteryPercent} sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: batteryPercent > 50 ? 'success.main' : batteryPercent > 20 ? 'warning.main' : 'error.main' } }} />
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 0.5, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)' }}>
                      <Typography variant="caption" color="text.secondary">Voltage</Typography>
                      <Typography variant="body2" fontWeight={600}>{batteryVoltage.toFixed(1)}V</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 0.5, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)' }}>
                      <Typography variant="caption" color="text.secondary">Current</Typography>
                      <Typography variant="body2" fontWeight={600}>{batteryCurrent.toFixed(2)}A</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 0.5, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)' }}>
                      <Typography variant="caption" color="text.secondary">Temp</Typography>
                      <Typography variant="body2" fontWeight={600}>{batteryTemp.toFixed(1)}°C</Typography>
                    </Paper>
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">Status: {powerStatus}</Typography>
                  <Typography variant="caption" color={powerHealth === 'good' ? 'success.main' : 'warning.main'}>Health: {powerHealth}</Typography>
                </Box>
                {batteryLocation && <Typography variant="caption" color="text.secondary" display="block">Location: {batteryLocation}</Typography>}
                {batterySerial && <Typography variant="caption" color="text.secondary" display="block">Serial: {batterySerial}</Typography>}
              </Box>
              
              {/* System Resources */}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Memory /><Typography variant="body2">CPU</Typography></Box>
                  <Typography variant="body2" fontWeight={600}>{cpuPercent.toFixed(0)}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={cpuPercent} sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: cpuPercent > 80 ? 'error.main' : 'success.main' } }} />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Storage /><Typography variant="body2">Memory</Typography></Box>
                  <Typography variant="body2" fontWeight={600}>{memoryPercent.toFixed(0)}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={memoryPercent} sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: memoryPercent > 80 ? 'error.main' : 'success.main' } }} />
                <Typography variant="caption" color="text.secondary">{formatMemory(memoryMb)}{memoryTotalMb > 0 ? ` / ${formatMemory(memoryTotalMb)}` : ''}</Typography>
              </Box>
              
              {/* Environment */}
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>Environment</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Thermostat fontSize="small" color="info" />
                      <Typography variant="caption" color="text.secondary">Temp</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600}>{envTemp.toFixed(1)}°C</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <WaterDrop fontSize="small" color="primary" />
                      <Typography variant="caption" color="text.secondary">Humidity</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600}>{(envHumidity * 100).toFixed(0)}%</Typography>
                  </Paper>
                </Grid>
              </Grid>
              {firmwareVersion && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Firmware: {firmwareVersion}
                </Typography>
              )}
              
              {/* Position */}
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>Position</Typography>
              <Grid container spacing={1}>
                <Grid item xs={4}><Paper sx={{ p: 0.5, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)' }}><Typography variant="caption" color="text.secondary">X</Typography><Typography variant="body2" fontWeight={600}>{normalizedPose?.x?.toFixed(2) ?? '--'}</Typography></Paper></Grid>
                <Grid item xs={4}><Paper sx={{ p: 0.5, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)' }}><Typography variant="caption" color="text.secondary">Y</Typography><Typography variant="body2" fontWeight={600}>{normalizedPose?.y?.toFixed(2) ?? '--'}</Typography></Paper></Grid>
                <Grid item xs={4}><Paper sx={{ p: 0.5, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)' }}><Typography variant="caption" color="text.secondary">θ</Typography><Typography variant="body2" fontWeight={600}>{normalizedPose?.theta != null ? (normalizedPose.theta * 180 / Math.PI).toFixed(0) : '--'}°</Typography></Paper></Grid>
              </Grid>
              
              {/* Shutdown/Reboot Buttons */}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="outlined" 
                  color="warning" 
                  size="small" 
                  startIcon={<PowerSettingsNew />}
                  onClick={() => setRebootDialogOpen(true)}
                  disabled={!isOnline}
                  fullWidth
                >
                  Reboot
                </Button>
                <Button 
                  variant="outlined" 
                  color="error" 
                  size="small" 
                  startIcon={<PowerSettingsNew />}
                  onClick={() => setShutdownDialogOpen(true)}
                  disabled={!isOnline}
                  fullWidth
                >
                  Shutdown
                </Button>
              </Box>
            </CardContent>
          </Card>
          <Card><CardContent><Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}><Speed /><Typography variant="body2">Max Speed: {maxSpeed.toFixed(1)} m/s</Typography></Box><Slider value={maxSpeed} onChange={(_, v) => setMaxSpeed(v as number)} min={0.1} max={1.5} step={0.1} disabled={!isOnline} /></CardContent></Card>
        </Grid>

        <Grid item xs={12} md={8}>
          {/* v6.3.47: the map sits directly above the controls instead of at
              the bottom of the page. Previously an operator had to scroll past
              the whole control panel to see where the robot was — unusable
              while driving. */}
          <Box sx={{ mb: 3 }}>
            <LiveMap
              robotSerial={robot.serial_number}
              mapId={currentMapId}
              initialPose={normalizedPose}
              localized={isLocalized}
              height={360}
            />
          </Box>
          <Card>
            <CardContent>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                <Tab label="Teleoperation" />
                <Tab label="Navigation" />
                <Tab label="Waypoints" />
                <Tab label="Settings" />
              </Tabs>

              {activeTab === 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
                    Use WASD or arrow keys • Hold to move continuously • SPACE for emergency stop
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, textAlign: 'center', display: 'block' }}>
                    Command rate: {config.command_rate_hz} Hz (adjust in Settings)
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <Button variant="contained" color="error" size="large" startIcon={<Warning />} onClick={handleEmergencyStop} disabled={!isOnline} sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}>EMERGENCY STOP</Button>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <IconButton 
                        size="large" 
                        disabled={!isOnline} 
                        onMouseDown={() => handleKeyDown('w')} 
                        onMouseUp={() => handleKeyUp('w')} 
                        onMouseLeave={() => handleKeyUp('w')}
                        onTouchStart={(e) => { e.preventDefault(); handleKeyDown('w'); }}
                        onTouchEnd={(e) => { e.preventDefault(); handleKeyUp('w'); }}
                        sx={{ width: 64, height: 64, bgcolor: activeKeys.has('w') ? 'primary.main' : 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'primary.main' } }}
                      >
                        <ArrowUpward sx={{ fontSize: 32 }} />
                      </IconButton>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton 
                          size="large" 
                          disabled={!isOnline} 
                          onMouseDown={() => handleKeyDown('a')} 
                          onMouseUp={() => handleKeyUp('a')} 
                          onMouseLeave={() => handleKeyUp('a')}
                          onTouchStart={(e) => { e.preventDefault(); handleKeyDown('a'); }}
                          onTouchEnd={(e) => { e.preventDefault(); handleKeyUp('a'); }}
                          sx={{ width: 64, height: 64, bgcolor: activeKeys.has('a') ? 'primary.main' : 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'primary.main' } }}
                        >
                          <ArrowBack sx={{ fontSize: 32 }} />
                        </IconButton>
                        <IconButton size="large" disabled={!isOnline} onClick={handleEmergencyStop} sx={{ width: 64, height: 64, bgcolor: 'error.main' }}><Stop sx={{ fontSize: 32 }} /></IconButton>
                        <IconButton 
                          size="large" 
                          disabled={!isOnline} 
                          onMouseDown={() => handleKeyDown('d')} 
                          onMouseUp={() => handleKeyUp('d')} 
                          onMouseLeave={() => handleKeyUp('d')}
                          onTouchStart={(e) => { e.preventDefault(); handleKeyDown('d'); }}
                          onTouchEnd={(e) => { e.preventDefault(); handleKeyUp('d'); }}
                          sx={{ width: 64, height: 64, bgcolor: activeKeys.has('d') ? 'primary.main' : 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'primary.main' } }}
                        >
                          <ArrowForward sx={{ fontSize: 32 }} />
                        </IconButton>
                      </Box>
                      <IconButton 
                        size="large" 
                        disabled={!isOnline} 
                        onMouseDown={() => handleKeyDown('s')} 
                        onMouseUp={() => handleKeyUp('s')} 
                        onMouseLeave={() => handleKeyUp('s')}
                        onTouchStart={(e) => { e.preventDefault(); handleKeyDown('s'); }}
                        onTouchEnd={(e) => { e.preventDefault(); handleKeyUp('s'); }}
                        sx={{ width: 64, height: 64, bgcolor: activeKeys.has('s') ? 'primary.main' : 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'primary.main' } }}
                      >
                        <ArrowDownward sx={{ fontSize: 32 }} />
                      </IconButton>
                    </Box>
                  </Box>
                  {activeKeys.size > 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Sending at {config.command_rate_hz} Hz: Linear={lastCommand.linear.toFixed(2)}, Angular={lastCommand.angular.toFixed(2)}
                    </Alert>
                  )}
                </Box>
              )}

              {activeTab === 1 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Send navigation goal</Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={4}><TextField label="X (m)" type="number" value={navGoal.x} fullWidth size="small" onChange={(e) => setNavGoal({ ...navGoal, x: parseFloat(e.target.value) || 0 })} /></Grid>
                    <Grid item xs={4}><TextField label="Y (m)" type="number" value={navGoal.y} fullWidth size="small" onChange={(e) => setNavGoal({ ...navGoal, y: parseFloat(e.target.value) || 0 })} /></Grid>
                    <Grid item xs={4}><TextField label="θ (deg)" type="number" value={navGoal.theta} fullWidth size="small" onChange={(e) => setNavGoal({ ...navGoal, theta: parseFloat(e.target.value) || 0 })} /></Grid>
                  </Grid>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="contained" startIcon={<Navigation />} onClick={handleNavGoal} disabled={!isOnline} fullWidth>Send Goal</Button>
                    <Button variant="outlined" color="warning" startIcon={<Cancel />} onClick={handleCancelNav} disabled={!isOnline}>Cancel</Button>
                  </Box>
                </Box>
              )}

              {activeTab === 2 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Send robot to saved waypoints. Waypoints are loaded from the current map.
                  </Typography>
                  
                  {/* Localization warning */}
                  {!isLocalized && isOnline && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Robot is not localized. Please confirm at charger before navigating.
                    </Alert>
                  )}
                  
                  {waypoints.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No waypoints available. To use waypoints:<br/>
                      1. Go to <strong>Maps</strong> page and edit a map to add waypoints<br/>
                      2. Click <strong>Load</strong> on the map to load it on this robot
                    </Alert>
                  ) : (
                    <Grid container spacing={2}>
                      {waypoints.map((wp) => {
                        const isChargingPoint = wp.name.toLowerCase().includes('charg');
                        const isHomePoint = wp.name.toLowerCase() === 'home';
                        const canNavigate = isOnline && isLocalized;
                        // "Active" = robot is currently driving to THIS waypoint.
                        // NOT "this is the charging point" — that was the old buggy
                        // logic that left "charging" green forever.
                        const isActiveTarget = activeWaypointName === wp.name
                          && ['navigating', 'active', 'in_progress', 'running'].includes(
                              String(mapManagerStatus.navStatus || '').toLowerCase());
                        // Subtle type tint (icon only) vs strong active highlight (card)
                        const typeIconColor = isChargingPoint ? 'success.main'
                                             : isHomePoint ? 'primary.main'
                                             : 'text.secondary';
                        const cardBg = isActiveTarget ? 'rgba(0, 212, 170, 0.18)' : 'rgba(255,255,255,0.03)';
                        const cardBorder = isActiveTarget ? 'primary.main' : 'divider';

                        return (
                          <Grid item xs={6} sm={4} md={3} key={wp.id}>
                            <Paper
                              sx={{
                                p: 2,
                                textAlign: 'center',
                                cursor: canNavigate ? 'pointer' : 'not-allowed',
                                opacity: canNavigate ? 1 : 0.5,
                                transition: 'all 0.2s',
                                bgcolor: cardBg,
                                border: '1px solid',
                                borderColor: cardBorder,
                                borderWidth: isActiveTarget ? 2 : 1,
                                '&:hover': canNavigate ? {
                                  bgcolor: isActiveTarget ? 'rgba(0, 212, 170, 0.25)' : 'rgba(255,255,255,0.08)',
                                  transform: 'scale(1.02)',
                                } : {}
                              }}
                              onClick={() => canNavigate && handleGoToWaypoint(wp)}
                            >
                              <LocationOn 
                                sx={{ 
                                  fontSize: 40, 
                                  color: typeIconColor,
                                  mb: 1 
                                }} 
                              />
                              <Typography variant="subtitle1" fontWeight={600} noWrap>
                                {wp.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                ({wp.x.toFixed(2)}, {wp.y.toFixed(2)})
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                θ: {(wp.theta * 180 / Math.PI).toFixed(0)}°
                              </Typography>
                              <Button 
                                variant="contained" 
                                size="small" 
                                startIcon={<Navigation />}
                                onClick={(e) => { e.stopPropagation(); handleGoToWaypoint(wp); }}
                                disabled={!canNavigate}
                                sx={{ mt: 1 }}
                                color={isChargingPoint ? 'success' : isHomePoint ? 'primary' : 'inherit'}
                                fullWidth
                              >
                                Go
                              </Button>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                  
                  {/* Emergency Stop for Waypoints */}
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(244, 67, 54, 0.1)', borderRadius: 2, border: '1px solid', borderColor: 'error.main' }}>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Emergency Controls
                    </Typography>
                    <Button
                      variant="contained"
                      color="error"
                      size="large"
                      fullWidth
                      startIcon={<Stop />}
                      onClick={handleEmergencyStop}
                      disabled={!isOnline}
                      sx={{ 
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        '&:hover': { bgcolor: 'error.dark' }
                      }}
                    >
                      EMERGENCY STOP
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                      Immediately stops all robot movement
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Button variant="outlined" startIcon={<Navigation />} onClick={() => navigate('/maps')} fullWidth>
                    Go to Maps to Load/Edit Waypoints
                  </Button>
                </Box>
              )}

              {activeTab === 3 && (
                <Box>
                  {/* v6.3.50 */}
                  <AutoDockSettings
                    robotId={robot.id}
                    waypoints={waypoints.map((w: any) => w.name || w.label).filter(Boolean)}
                  />
                  <Divider sx={{ my: 4 }} />
                  <Typography variant="h6" gutterBottom>Teleoperation Settings</Typography>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Tune />
                      <Typography variant="body2">Command Rate: {config.command_rate_hz} Hz</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      How many teleop commands per second to send while keys are pressed (5-20 Hz)
                    </Typography>
                    <Slider 
                      value={config.command_rate_hz} 
                      onChange={(_, v) => setConfig({ ...config, command_rate_hz: v as number })} 
                      min={5} 
                      max={20} 
                      step={1}
                      marks={[
                        { value: 5, label: '5' },
                        { value: 10, label: '10' },
                        { value: 15, label: '15' },
                        { value: 20, label: '20' },
                      ]}
                    />
                  </Box>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="h6" gutterBottom>Auto-Charge Configuration</Typography>
                  <FormControlLabel control={<Switch checked={config.auto_charge_enabled} onChange={(e) => setConfig({ ...config, auto_charge_enabled: e.target.checked })} />} label="Enable Auto-Charge" sx={{ mb: 2 }} />
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" gutterBottom>Charge Threshold: {config.charge_threshold}%</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Robot returns to dock when battery falls below this level</Typography>
                    <Slider value={config.charge_threshold} onChange={(_, v) => setConfig({ ...config, charge_threshold: v as number })} min={10} max={50} step={5} disabled={!config.auto_charge_enabled} />
                  </Box>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" gutterBottom>Default Max Speed: {config.max_speed} m/s</Typography>
                    <Slider value={config.max_speed} onChange={(_, v) => setConfig({ ...config, max_speed: v as number })} min={0.1} max={1.5} step={0.1} />
                  </Box>
                  <Button variant="contained" startIcon={configSaved ? <CheckCircle /> : <Save />} onClick={handleSaveConfig} color={configSaved ? 'success' : 'primary'}>{configSaved ? 'Saved!' : 'Save Configuration'}</Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

      </Grid>
      
      {/* Shutdown Confirmation Dialog */}
      <Dialog open={shutdownDialogOpen} onClose={() => setShutdownDialogOpen(false)}>
        <DialogTitle sx={{ color: 'error.main' }}>Shutdown Robot?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to shutdown the robot <strong>{robot.friendly_name || robot.serial_number}</strong>?
          </Typography>
          <Typography sx={{ mt: 2 }} color="text.secondary">
            The robot will power off completely. You will need physical access to turn it back on.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShutdownDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleShutdown}>Shutdown</Button>
        </DialogActions>
      </Dialog>
      
      {/* Reboot Confirmation Dialog */}
      <Dialog open={rebootDialogOpen} onClose={() => setRebootDialogOpen(false)}>
        <DialogTitle sx={{ color: 'warning.main' }}>Reboot Robot?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reboot the robot <strong>{robot.friendly_name || robot.serial_number}</strong>?
          </Typography>
          <Typography sx={{ mt: 2 }} color="text.secondary">
            The robot will restart and should come back online in a few minutes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebootDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleReboot}>Reboot</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RobotControl;
